/**
 * CashFlowWidget — resumen del flujo de caja del día.
 * Útil para que admin y cajero vean en tiempo real cuánto entró/salió hoy
 * agrupado por categoría contable. Se refresca cada 60s.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowUpRight, ArrowDownRight, TrendingUp, RefreshCw, Loader2 } from "lucide-react";
import { Link } from "react-router-dom";

const money = (n) =>
  `RD$${Number(n || 0).toLocaleString("es-DO", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function CashFlowWidget() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/cash-sessions/dashboard/cash-flow-today");
      setData(r.data);
    } catch (e) {
      // El módulo cash_sessions puede estar off → silencioso
      if (!e.isModuleDisabled) console.warn("CashFlow:", e);
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, []);

  if (!data) return null;

  const netPositive = data.net >= 0;
  return (
    <div
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
      data-testid="cashflow-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <TrendingUp className="w-4 h-4 text-primary" />
          <h3 className="font-bold">Flujo de caja hoy</h3>
          <Badge variant="outline" className="text-[10px]">{data.date}</Badge>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="ghost" onClick={load} disabled={loading} data-testid="cashflow-refresh">
            {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
          <Button asChild size="sm" variant="ghost">
            <Link to="/cash-sessions" data-testid="cashflow-view-sessions">Ver turnos</Link>
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-3 mb-4">
        <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 p-3">
          <div className="flex items-center gap-1 text-xs text-emerald-700 dark:text-emerald-400 mb-1">
            <ArrowUpRight className="w-3.5 h-3.5" /> Entró
          </div>
          <div className="text-xl font-black text-emerald-700 dark:text-emerald-400" data-testid="cashflow-total-in">{money(data.total_in)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">{data.sales_count} ventas · {Math.round(data.receipts_in && (data.receipts_in / data.total_in * 100))}% recibos</div>
        </div>
        <div className="rounded-md bg-rose-50 dark:bg-rose-950/30 p-3">
          <div className="flex items-center gap-1 text-xs text-rose-700 dark:text-rose-400 mb-1">
            <ArrowDownRight className="w-3.5 h-3.5" /> Salió
          </div>
          <div className="text-xl font-black text-rose-700 dark:text-rose-400" data-testid="cashflow-total-out">{money(data.total_out)}</div>
          <div className="text-[11px] text-muted-foreground mt-1">{data.outflows_by_category.length} categoría(s)</div>
        </div>
        <div className={`rounded-md p-3 ${netPositive ? "bg-primary/5 border border-primary/20" : "bg-amber-50 dark:bg-amber-950/30"}`}>
          <div className="text-xs text-muted-foreground mb-1">Neto del día</div>
          <div className={`text-xl font-black ${netPositive ? "text-primary" : "text-amber-700 dark:text-amber-400"}`} data-testid="cashflow-net">
            {netPositive ? "+" : ""}{money(data.net)}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        <div>
          <div className="font-bold text-xs text-muted-foreground mb-2">INGRESOS POR CATEGORÍA</div>
          <ul className="space-y-1">
            {data.inflows_by_category.filter(c => c.amount > 0).map((c) => (
              <li key={c.category} className="flex justify-between items-center">
                <span className="truncate">{c.category}</span>
                <b className="text-emerald-700 dark:text-emerald-400">+{money(c.amount)}</b>
              </li>
            ))}
            {data.inflows_by_category.every(c => c.amount === 0) && (
              <li className="text-muted-foreground italic">Sin ingresos aún</li>
            )}
          </ul>
        </div>
        <div>
          <div className="font-bold text-xs text-muted-foreground mb-2">EGRESOS POR CATEGORÍA</div>
          <ul className="space-y-1">
            {data.outflows_by_category.map((c) => (
              <li key={c.category} className="flex justify-between items-center">
                <span className="truncate">{c.category}</span>
                <b className="text-rose-700 dark:text-rose-400">-{money(c.amount)}</b>
              </li>
            ))}
            {data.outflows_by_category.length === 0 && (
              <li className="text-muted-foreground italic">Sin egresos aún</li>
            )}
          </ul>
        </div>
      </div>
    </div>
  );
}
